import {
  SITE_TYPE_LABELS_BY_COUNTRY,
  SITE_FACET_LABELS_BY_COUNTRY,
} from "../../utils/labels.js";

/**
 * Country-to-scanner mapping for country-aware facility search.
 *
 * Used by:
 *  - FacilitySearchCombobox (pills, placeholder, helperText)
 *  - /api/facilities (country param → agencyIds resolution)
 *  - Landing page loaders (landingLoaderCountry)
 *  - getCountryByScanner (runtime scanner→country inversion for legacy callers)
 *  - Reservation-system directory rendered on /new and the FAQ
 *    ("What campgrounds can I watch?") — see reservationSystems below
 *
 * Scanner names must match the Agency.scanner enum in the database.
 * Country keys mirror the Country enum in prisma/schema.prisma.
 *
 * reservationSystems is the single source of truth for the public-facing,
 * curated list of booking portals we monitor per country
 * ({ name, href, scanners, tier }). `scanners` connects each public booking
 * system to the Agency rows whose live Facility counts appear in the /new
 * directory. It is deliberately distinct from scanners[] (internal enum names
 * that include aggregators and have no display name or URL). `tier` is one of
 * RESERVATION_SYSTEM_TIERS and sets where the system appears in the directory
 * (see sortReservationSystems), so array order here does not matter. An
 * optional `sortName` alphabetizes a system whose display name would land it
 * in the wrong place (a brand name, or a club section that should follow its
 * parent club); it is never shown.
 *
 * siteTypeLabels / siteFacetLabels are re-exported from utils/labels.js
 * (scanner-safe module) for discoverability alongside other per-country config.
 * Edit those maps in utils/labels.js — this file imports them.
 */
export const COUNTRIES = {
  ca: {
    label: "Canada",
    flag: "🇨🇦",
    scanners: [
      "ParksCanada",
      "BCParks",
      "BCParksDayUse",
      "OntarioParks",
      "AlbertaParks",
      "ACC",
      "ACCVancouverIsland",
      "ACCWhistler",
      "SpearheadHuts",
      "MBParks",
      "NBParks",
      "NSParks",
      "NLParks",
      "YTParks",
      "NWTParks",
      "LongPointRegion",
      "AlgonquinHighlands",
      "MaitlandValley",
      "SKParks",
      "PEIParks",
      "NUParks",
      "QCParks",
      "UpperThames",
      "GatineauPark",
      "StClairRegion",
      "Campspot",
      "WebRezPro",
      "MountKiddRV",
      "TestAgency",
    ],
    placeholder: "e.g. Lake O'Hara, Berg Lake, Killarney...",
    helperText:
      "Currently monitoring Parks Canada, BC Parks, Ontario Parks, and more.",
    reservationSystems: [
      {
        name: "Parks Canada",
        href: "https://reservation.pc.gc.ca/",
        scanners: ["ParksCanada"],
        tier: "national",
      },
      {
        name: "BC Parks",
        href: "https://camping.bcparks.ca/",
        scanners: ["BCParks", "BCParksDayUse"],
        tier: "state",
      },
      {
        name: "Ontario Parks",
        href: "https://reservations.ontarioparks.com/",
        scanners: ["OntarioParks"],
        tier: "state",
      },
      {
        name: "Manitoba Parks",
        href: "https://manitoba.goingtocamp.com/",
        scanners: ["MBParks"],
        tier: "state",
      },
      {
        name: "Nova Scotia Parks",
        href: "https://novascotia.goingtocamp.com/",
        scanners: ["NSParks"],
        tier: "state",
      },
      {
        name: "New Brunswick Parks",
        href: "https://reservations.parcsnbparks.ca/",
        scanners: ["NBParks"],
        tier: "state",
      },
      {
        name: "Newfoundland and Labrador Parks",
        href: "https://www.nlcamping.ca/",
        scanners: ["NLParks"],
        tier: "state",
      },
      {
        name: "Yukon Parks",
        href: "https://yukon.goingtocamp.com/",
        scanners: ["YTParks"],
        tier: "state",
      },
      {
        name: "Northwest Territories Parks",
        href: "https://nwtparks.goingtocamp.com/",
        scanners: ["NWTParks"],
        tier: "state",
      },
      {
        name: "Gatineau Park",
        href: "https://reservations.ncc-ccn.gc.ca/",
        scanners: ["GatineauPark"],
        tier: "state",
      },
      {
        name: "Upper Thames River Conservation Authority",
        href: "https://upperthames.goingtocamp.com/",
        scanners: ["UpperThames"],
        tier: "municipal",
      },
      {
        name: "Maitland Valley Conservation Authority",
        href: "https://maitlandvalley.goingtocamp.com/",
        scanners: ["MaitlandValley"],
        tier: "municipal",
      },
      {
        name: "Long Point Region Conservation Authority",
        href: "https://longpoint.goingtocamp.com/",
        scanners: ["LongPointRegion"],
        tier: "municipal",
      },
      {
        name: "St. Clair Region Conservation Authority",
        href: "https://stclair.goingtocamp.com/",
        scanners: ["StClairRegion"],
        tier: "municipal",
      },
      {
        name: "Algonquin Highlands Trails",
        href: "https://ahtrails.ca/",
        scanners: ["AlgonquinHighlands"],
        tier: "municipal",
      },
      {
        name: "Alpine Club of Canada (ACC)",
        href: "https://alpineclubofcanada.ca/huts/",
        scanners: ["ACC"],
        tier: "private",
      },
      {
        name: "ACC Vancouver Island — Hišimy̓awiƛ",
        sortName: "Alpine Club of Canada Vancouver Island",
        href: "https://accvi.ca/5040-peak-hut/bookthehut/",
        scanners: ["ACCVancouverIsland"],
        tier: "private",
      },
      {
        name: "ACC Whistler — Wendy Thompson Hut",
        sortName: "Alpine Club of Canada Whistler",
        href: "https://booking.accwhistler.ca/book.html",
        scanners: ["ACCWhistler"],
        tier: "private",
      },
      {
        name: "Spearhead Huts Society",
        href: "https://hotels.cloudbeds.com/en/reservation/7cNBuE?currency=cad",
        scanners: ["SpearheadHuts"],
        tier: "private",
      },
      {
        name: "Columbia Valley Hut Society (CVHS)",
        href: "https://cvhsinfo.org/",
        scanners: ["WebRezPro"],
        tier: "private",
        facilitySubName: "Columbia Valley Hut Society",
      },
    ],
    siteTypeLabels: SITE_TYPE_LABELS_BY_COUNTRY.ca,
    siteFacetLabels: SITE_FACET_LABELS_BY_COUNTRY.ca,
  },
  us: {
    label: "United States",
    flag: "🇺🇸",
    scanners: [
      "RecDotGov",
      "FloridaParks",
      "ReserveCalifornia",
      "Melanzana",
      "YellowstoneLodges",
      "WashingtonParks",
      "MichiganStateParks",
      "WisconsinStateParks",
      "WyomingParks",
      "HawaiiStateParks",
      "ArizonaStateParks",
      "OhioStateParks",
      "VirginiaStateParks",
      "NorthDakotaStateParks",
      "MinnesotaStateParks",
      "NevadaStateParks",
      "MissouriStateParks",
      "IllinoisStateParks",
    ],
    placeholder: "e.g. Yosemite, Glacier, Enchantments, Bahia Honda...",
    helperText:
      "Currently monitoring Recreation.gov national parks, Florida State Parks, Reserve California, Yellowstone National Park Lodges, Washington State Parks, Michigan State Parks, Wisconsin State Parks, Wyoming State Parks, Hawaii State Parks, Arizona State Parks and Trails, Ohio State Parks, Virginia State Parks, North Dakota State Parks, Minnesota State Parks, Nevada State Parks, Missouri State Parks, and Illinois State Parks.",
    reservationSystems: [
      {
        name: "Recreation.gov",
        href: "https://www.recreation.gov",
        scanners: ["RecDotGov"],
        tier: "national",
      },
      {
        name: "Florida State Parks",
        href: "https://reserve.floridastateparks.org",
        scanners: ["FloridaParks"],
        tier: "state",
      },
      {
        name: "Reserve California",
        sortName: "California State Parks",
        href: "https://www.reservecalifornia.com",
        scanners: ["ReserveCalifornia"],
        tier: "state",
      },
      {
        name: "Yellowstone National Park Lodges",
        href: "https://secure.yellowstonenationalparklodges.com/booking/lodging",
        scanners: ["YellowstoneLodges"],
        tier: "private",
      },
      {
        name: "Washington State Parks",
        href: "https://washington.goingtocamp.com",
        scanners: ["WashingtonParks"],
        tier: "state",
      },
      {
        name: "Michigan State Parks",
        href: "https://midnrreservations.com",
        scanners: ["MichiganStateParks"],
        tier: "state",
      },
      {
        name: "Wisconsin State Parks",
        href: "https://wisconsin.goingtocamp.com",
        scanners: ["WisconsinStateParks"],
        tier: "state",
      },
      {
        name: "Wyoming State Parks",
        href: "https://reserve.wyoming.gov/",
        scanners: ["WyomingParks"],
        tier: "state",
      },
      {
        name: "Hawaii State Parks",
        href: "https://explore.ehawaii.gov/web/",
        scanners: ["HawaiiStateParks"],
        tier: "state",
      },
      {
        name: "Arizona State Parks and Trails",
        href: "https://azstateparks.com/reserve/",
        scanners: ["ArizonaStateParks"],
        tier: "state",
      },
      {
        name: "Ohio State Parks",
        href: "https://reserveohio.com/OhioCampWeb/",
        scanners: ["OhioStateParks"],
        tier: "state",
      },
      {
        name: "Virginia State Parks",
        href: "https://reservevaparks.com/web/",
        scanners: ["VirginiaStateParks"],
        tier: "state",
      },
      {
        name: "North Dakota State Parks",
        href: "https://reservendparks.com/Web/",
        scanners: ["NorthDakotaStateParks"],
        tier: "state",
      },
      {
        name: "Minnesota State Parks",
        href: "https://reservemn.usedirect.com/MinnesotaWeb/",
        scanners: ["MinnesotaStateParks"],
        tier: "state",
      },
      {
        name: "Nevada State Parks",
        href: "https://www.reservenevada.com/NevadaWeb/Default.aspx",
        scanners: ["NevadaStateParks"],
        tier: "state",
      },
      {
        name: "Missouri State Parks",
        href: "https://icampmo1.usedirect.com/MSPWeb/",
        scanners: ["MissouriStateParks"],
        tier: "state",
      },
      {
        name: "Illinois State Parks",
        href: "https://recreation.exploremoreil.com/IllinoisWeb/",
        scanners: ["IllinoisStateParks"],
        tier: "state",
      },
    ],
    siteTypeLabels: SITE_TYPE_LABELS_BY_COUNTRY.us,
    siteFacetLabels: SITE_FACET_LABELS_BY_COUNTRY.us,
  },
  au: {
    label: "Australia",
    flag: "🇦🇺",
    scanners: [
      "NSWParks",
      "ParksVictoria",
      "GORCAPA",
      "QueenslandParks",
      "ThorsborneTrail",
      "WAParks",
      "TASParks",
      "NTParks",
      "ACTParks",
      "SAParks",
      "RACParksResorts",
      "NRMAParksResorts",
      "IngeniaHolidays",
      "StayRottnest",
      "YardieHomestead",
      "TasmanHolidayParksAU",
      "ReflectionsHolidayParks",
      "SummerstarTouristParks",
      "BIG4HolidayParks",
      "ElQuestro",
      "GdayParks",
      "DiscoveryParks",
      "NingalooCoralBay",
      "NingalooCaravanHolidayResort",
      "PeoplesParkCoralBay",
      "BullaraStation",
      "BarnHillStation",
      "KalbarriAnchorage",
      "BathersParadise",
      "EsperancePinkLake",
      "ShireOfCoorow",
      "SpaceToCo",
      "SunshineCoastHolidayParks",
    ],
    placeholder:
      "e.g. Osprey Bay, Tidal River, Rottnest Island, Overland Track...",
    helperText:
      "National parks, caravan parks, and holiday parks across Australia.",
    reservationSystems: [
      {
        name: "NSW National Parks",
        href: "https://www.nationalparks.nsw.gov.au/",
        scanners: ["NSWParks"],
        tier: "state",
      },
      {
        name: "Parks Victoria",
        href: "https://bookings.parks.vic.gov.au/",
        scanners: ["ParksVictoria"],
        tier: "state",
      },
      {
        name: "Great Ocean Road Coast and Parks Authority",
        href: "https://www.greatoceanroadauthority.vic.gov.au/Land-We-Manage/National-Parks/Great-Otway-National-Park",
        scanners: ["GORCAPA"],
        tier: "state",
      },
      {
        name: "Queensland Parks",
        href: "https://book.parks.qld.gov.au/camping",
        scanners: ["QueenslandParks", "ThorsborneTrail"],
        tier: "state",
      },
      {
        name: "South Australia Parks",
        href: "https://www.parks.sa.gov.au/",
        scanners: ["SAParks"],
        tier: "state",
      },
      {
        name: "Park Stay WA",
        href: "https://parkstay.dbca.wa.gov.au",
        scanners: ["WAParks"],
        tier: "state",
      },
      {
        name: "Tassie Parks",
        href: "https://parks.tas.gov.au/",
        scanners: ["TASParks"],
        tier: "state",
      },
      {
        name: "Northern Territory Parks",
        href: "https://nt.gov.au/parks-reserves",
        scanners: ["NTParks"],
        tier: "state",
      },
      {
        name: "ACT Parks",
        href: "https://actparks.bookeasy.com/book",
        scanners: ["ACTParks"],
        tier: "state",
      },
      {
        name: "Stay Rottnest",
        href: "https://bookings.stayrottnest.com/rates-room1?hotel=62607",
        scanners: ["StayRottnest"],
        tier: "state",
      },
      {
        name: "RAC Parks & Resorts",
        href: "https://parksandresorts.rac.com.au/",
        scanners: ["RACParksResorts"],
        tier: "private",
      },
      {
        name: "NRMA Parks and Resorts",
        href: "https://www.nrmaparksandresorts.com.au/our-parks/",
        scanners: ["NRMAParksResorts"],
        tier: "private",
      },
      {
        name: "Ingenia Holiday Parks",
        href: "https://www.ingeniaholidays.com.au/our-parks",
        scanners: ["IngeniaHolidays"],
        tier: "private",
      },
      {
        name: "Yardie Homestead Caravan Park",
        href: "https://bookingsau.newbook.cloud/yardie/",
        scanners: ["YardieHomestead"],
        tier: "private",
      },
      {
        name: "Ningaloo Coral Bay",
        href: "https://www.ningaloocoralbay.com/",
        scanners: ["NingalooCoralBay"],
        tier: "private",
      },
      {
        name: "Ningaloo Caravan & Holiday Resort",
        href: "https://bookingsau.newbook.cloud/online/ningaloo_caravan__holiday_resort",
        scanners: ["NingalooCaravanHolidayResort"],
        tier: "private",
      },
      {
        name: "Peoples Park Coral Bay",
        href: "https://peoplesparkcoralbay.com.au/book-now/",
        scanners: ["PeoplesParkCoralBay"],
        tier: "private",
      },
      {
        name: "Bullara Station",
        href: "https://bookingsau.newbook.cloud/bullarastation/index.php?place_booking",
        scanners: ["BullaraStation"],
        tier: "private",
      },
      {
        name: "Barn Hill Station",
        href: "https://bookingsau.newbook.cloud/barnhill/index.php?place_booking",
        scanners: ["BarnHillStation"],
        tier: "private",
      },
      {
        name: "Kalbarri Anchorage Holiday Village",
        href: "https://bookingsau.newbook.cloud/kalbarrianchorage/index.php?place_booking",
        scanners: ["KalbarriAnchorage"],
        tier: "private",
      },
      {
        name: "Bathers Paradise Caravan Park",
        href: "https://bookingsau.newbook.cloud/bathersparadise/index.php?place_booking",
        scanners: ["BathersParadise"],
        tier: "private",
      },
      {
        name: "Esperance Pink Lake Holiday Park",
        href: "https://esperancepinklake.com.au/book-online/",
        scanners: ["EsperancePinkLake"],
        tier: "private",
      },
      {
        name: "Tasman Holiday Parks (Australia)",
        href: "https://book.tasmanholidayparks.com/",
        scanners: ["TasmanHolidayParksAU"],
        tier: "private",
      },
      {
        name: "Shire of Coorow",
        href: "https://bookings-shireofcoorow.escapia.com/",
        scanners: ["ShireOfCoorow"],
        tier: "municipal",
      },
      {
        name: "Sandy Cape Recreation Park (Shire of Dandaragan)",
        href: "https://www.spacetoco.com/org/shire-of-dandaragan/stay",
        scanners: ["SpaceToCo"],
        tier: "municipal",
      },
      {
        name: "Sunshine Coast Holiday Parks",
        href: "https://www.sunshinecoastholidayparks.com.au/",
        scanners: ["SunshineCoastHolidayParks"],
        tier: "municipal",
      },
      {
        name: "Reflections Holidays",
        href: "https://reflectionsholidays.com.au/",
        scanners: ["ReflectionsHolidayParks"],
        tier: "private",
      },
      {
        name: "Summerstar Tourist Parks",
        href: "https://summerstar.com.au/",
        scanners: ["SummerstarTouristParks"],
        tier: "private",
      },
      {
        name: "BIG4 Holiday Parks",
        href: "https://www.big4.com.au/",
        scanners: ["BIG4HolidayParks"],
        tier: "private",
      },
      {
        name: "El Questro",
        href: "https://elquestro.com.au/",
        scanners: ["ElQuestro"],
        tier: "private",
      },
      {
        name: "G'day Parks",
        href: "https://gdayparks.com.au/",
        scanners: ["GdayParks"],
        tier: "private",
      },
      {
        name: "Discovery Parks",
        href: "https://www.discoveryholidayparks.com.au/",
        scanners: ["DiscoveryParks"],
        tier: "private",
      },
    ],
    siteTypeLabels: SITE_TYPE_LABELS_BY_COUNTRY.au,
    siteFacetLabels: SITE_FACET_LABELS_BY_COUNTRY.au,
  },
  nz: {
    label: "New Zealand",
    flag: "🇳🇿",
    scanners: [
      "NZDOC",
      "NZTop10",
      "AucklandCouncil",
      "TasmanHolidayParksNZ",
      "HampshireHolidayParks",
      "TimberTrailLodge",
    ],
    placeholder:
      "e.g. Milford Track, Mueller Hut, Tāwharanui, Papamoa Beach...",
    helperText: "Huts, holiday parks, and regional parks across New Zealand.",
    reservationSystems: [
      {
        name: "New Zealand DOC",
        href: "https://www.doc.govt.nz/",
        scanners: ["NZDOC"],
        tier: "national",
      },
      {
        name: "TOP 10 Holiday Parks",
        href: "https://top10.co.nz/",
        scanners: ["NZTop10"],
        tier: "private",
      },
      {
        name: "Auckland Council Regional Parks",
        href: "https://www.aucklandcouncil.govt.nz/en/parks-recreation/stay-at-park/find-accommodation.html",
        scanners: ["AucklandCouncil"],
        tier: "municipal",
      },
      {
        name: "Tasman Holiday Parks (New Zealand)",
        href: "https://book.tasmanholidayparks.com/",
        scanners: ["TasmanHolidayParksNZ"],
        tier: "private",
      },
      {
        name: "Hampshire Holiday Parks",
        href: "https://hampshireholidayparks.co.nz/",
        scanners: ["HampshireHolidayParks"],
        tier: "private",
      },
      {
        name: "Timber Trail Lodge",
        href: "https://www.timbertraillodge.co.nz/book-now",
        scanners: ["TimberTrailLodge"],
        tier: "private",
      },
    ],
    siteTypeLabels: SITE_TYPE_LABELS_BY_COUNTRY.nz,
    siteFacetLabels: SITE_FACET_LABELS_BY_COUNTRY.nz,
  },
};

export const COUNTRY_CODES = Object.keys(COUNTRIES);

// Directory order within a country, by who runs the booking system:
//  - national: national park systems (Parks Canada, Recreation.gov, DOC)
//  - state: state, provincial and territorial parks, including state
//    authorities (Rottnest Island Authority, Great Ocean Road Authority) and
//    Gatineau Park, which reads as a Quebec park though the federal National
//    Capital Commission runs it
//  - municipal: councils, townships and Ontario conservation authorities
//  - private: companies, clubs and non-profits, including concessioners that
//    sell lodging inside a national park (Yellowstone National Park Lodges)
export const RESERVATION_SYSTEM_TIERS = [
  "national",
  "state",
  "municipal",
  "private",
];

// Tier order first, then alphabetical by sortName when set, else by display
// name. The explicit "en" locale keeps server and client renders in the same
// order.
export function sortReservationSystems(systems) {
  return [...systems].sort(
    (a, b) =>
      RESERVATION_SYSTEM_TIERS.indexOf(a.tier) -
        RESERVATION_SYSTEM_TIERS.indexOf(b.tier) ||
      (a.sortName ?? a.name).localeCompare(b.sortName ?? b.name, "en"),
  );
}

// Public-facing count of booking systems we monitor, derived from
// reservationSystems so copy that cites it (FAQ, about, SEO answers)
// never drifts from the directory rendered on /new.
export const RESERVATION_SYSTEM_COUNT = COUNTRY_CODES.reduce(
  (count, code) => count + COUNTRIES[code].reservationSystems.length,
  0,
);

/**
 * Fail-loud scanner → country inversion. Used by callers that only have an
 * Agency.scanner value and need to derive country (e.g. transitional loaders
 * pre-dating the Agency.country column). Throws on unknown or ambiguous
 * scanner — both signal a misconfiguration in COUNTRIES.scanners[].
 *
 * Prefer reading agency.country directly once a loader selects it.
 */
export function getCountryByScanner(scanner) {
  const matches = COUNTRY_CODES.filter((code) =>
    COUNTRIES[code].scanners.includes(scanner),
  );
  if (matches.length === 0) {
    throw new Error(
      `getCountryByScanner: scanner "${scanner}" not found in any country's scanners list`,
    );
  }
  if (matches.length > 1) {
    throw new Error(
      `getCountryByScanner: scanner "${scanner}" ambiguous between [${matches.join(", ")}]`,
    );
  }
  return matches[0];
}
